import { getToolDisplayName } from "../../constants/tools"
import type { FailedStepStyle, ToolStep } from "./ToolCallGroup"

interface FailedStepPresentation {
  colorClass: string
  outcome: string
}

export const FAILED_STEP_PRESENTATION: Record<FailedStepStyle, FailedStepPresentation> = {
  report: { colorClass: "bg-destructive-strong", outcome: "failed" },
  neutral: { colorClass: "bg-foreground-muted", outcome: "not completed" },
}

interface GroupHeaderState {
  anyInterrupted: boolean
  isStreaming: boolean
  isTurnOpen: boolean
  stepCount: number
  runningStep: ToolStep | null
  failedStepCount: number
  failedStepStyle: FailedStepStyle
}

export function resolveDisplaySteps(steps: ToolStep[], isLive: boolean): ToolStep[] {
  if (isLive) return steps
  let mutated = false
  const resolved = steps.map((s) => {
    if (s.status !== "in_progress") return s
    mutated = true
    return { ...s, status: "done" as const, completedAt: s.completedAt ?? s.startedAt }
  })
  return mutated ? resolved : steps
}

export function runningStepLabel(step: ToolStep): string {
  const action = getToolDisplayName(step.toolName)
  return step.subject ? `${action} · ${step.subject}` : action
}

export function groupHeaderLabel(opts: GroupHeaderState): string {
  const progressLabel = groupProgressLabel(opts)
  if (opts.failedStepCount === 0) return progressLabel
  return `${progressLabel} · ${opts.failedStepCount} ${FAILED_STEP_PRESENTATION[opts.failedStepStyle].outcome}`
}

function groupProgressLabel(opts: GroupHeaderState): string {
  const { anyInterrupted, isStreaming, isTurnOpen, stepCount, runningStep } = opts
  if (anyInterrupted) return "Cancelled by user"
  if (isStreaming) return runningStep ? runningStepLabel(runningStep) : "Working on your response"
  if (isTurnOpen) return "Working in the background"
  const stepWord = `step${stepCount === 1 ? "" : "s"}`
  return `Completed in ${stepCount} ${stepWord}`
}

export function computeElapsedMs(steps: ToolStep[], now: number): number {
  if (steps.length === 0) return 0
  const startedAt = Math.min(...steps.map((s) => s.startedAt))
  const terminal = steps.every((s) => s.status !== "in_progress")
  if (terminal) {
    const completed = steps.map((s) => s.completedAt ?? s.startedAt)
    return Math.max(0, Math.max(...completed) - startedAt)
  }
  return Math.max(0, now - startedAt)
}
